<script setup lang="ts">
import { computed, ref } from 'vue';
import { useForm, useField } from 'vee-validate';
import { Applicant } from '@/application/shared/domain/Applicant';
import QuestionPersonFullName from '@/application/shared/infrastructure/components/questions/QuestionPersonFullName.vue';
import { QuestionEmail, QuestionSSN, QuestionYesNo } from '@hfc/ui-core';

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const props = defineProps<{
  value?: Partial<Applicant>,
  autoFocus?: boolean
  hideSuffix?: boolean
  excludeEmailConfirmation?: boolean
  obfuscateSsn?: boolean
  allApplicants?: Array<Applicant>
}>();

defineExpose({
  handleSubmit: runValidations
});

interface EmitInterface {
  (event: 'update:value', value: Partial<Applicant>): void
}

const emits = defineEmits<EmitInterface>();
const yesNoQuestionEngaged = ref(false);

const {
  value: hasSSN,
  errorMessage: hasSSNErrorMessage
} = useField('hasSSN', 'yes_no_required', { initialValue: props.value?.has_ssn });

const emitChange = (value: Partial<Applicant>): void => {
  emits('update:value', value);
};
const email = ref(props.value?.email);
const confirmEmail = ref(props.value?.confirm_email);

const handleEmailChange = (value: string): void => {
  emits('update:value', { email: value });
  email.value = value;
};
const handleConfirmEmailChange = (value: string): void => {
  emits('update:value', { confirm_email: value });
  confirmEmail.value = value;
};

const emitSSNChange = (value: string): void => {
  emitChange({ ssn: value });
};

const emitHasSSNChange = (value: boolean | undefined): void => {
  emitChange({ has_ssn: value ?? false });
  if (!value) {
    emitChange({ ssn: undefined });
  }
};

const handleYesNoQuestionChange = (value: boolean | undefined): void => {
  yesNoQuestionEngaged.value = value ?? false;
  emitHasSSNChange(value);
};

const allApplicantsSSNs = computed(() => {
  return props.allApplicants?.map((applicant: Applicant) => applicant.ssn ?? '').filter(Boolean);
});

const excludeEmailConfirmation = ref(props.excludeEmailConfirmation || false);
</script>

<template>
  <div class="flex flex-col">
    <QuestionPersonFullName :value="props.value" @update:value="emitChange" :auto-focus="autoFocus" :hide-suffix="hideSuffix" />
    <QuestionEmail id="mainEmail" name="email" :use-form-context="true" :rules="{ required: true, email: true, max: 40 }" :value="email" @update:value="handleEmailChange" />
    <QuestionEmail v-if="!excludeEmailConfirmation" id="confirmEmail" name="confirmEmail" :use-form-context="true" :rules="{ required: true, confirmed_insensitive: email, max: 40 }" :value="confirmEmail" prefix="Confirm" @update:value="handleConfirmEmailChange" />
    <div class="mb-8 mt-2">
      <QuestionYesNo question-text="Do you have a Social Security Number?" id="hasSSN" @update:modelValue="handleYesNoQuestionChange" name="hasSSN" v-model="hasSSN" />
      <span v-if="hasSSNErrorMessage" class="error">{{ hasSSNErrorMessage }}</span>
    </div>
    <div class="mb-8" v-if="hasSSN">
      <QuestionSSN id="ssn" name="ssn" :value="props.value?.ssn" :auto-focus="yesNoQuestionEngaged" @update:value="emitSSNChange" :obfuscate="obfuscateSsn" :ssn-not-to-be-repeated="allApplicantsSSNs"  required />
    </div>
  </div>
</template>
